/**
 * LoomSplitGrid — Feb 2026
 * =========================
 * Split-Head Production entry grid for Loom Production Entry.
 *
 * Renders one row per split-head plan (colour/variant) with independent
 * shift hours, calculated meters (using THIS shard's heads — NOT machine
 * total), and Kgs. Common Shift-1/Shift-2 controls cascade to every row
 * on toggle so operators can log a full parallel run in one click, then
 * override individual splits that stopped or started separately.
 *
 * Data-testids match the naming convention on ProductionEntry.jsx so the
 * testing agent can drive precise UI flows.
 */
import React, { useEffect, useMemo, useState } from "react";
import FormattedNumberInput from "@/components/FormattedNumberInput";

const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";

const meterFor = (row, common) => {
  const spd   = parseFloat(row.speed_rpm     || 0);
  const pcm   = parseFloat(row.picks_per_cm  || 0);
  const heads = parseFloat(row.heads_allocated || 0);
  const s1    = row.override ? parseFloat(row.shift1_hours || 0) : parseFloat(common.shift1_hours || 0);
  const s2    = row.override ? parseFloat(row.shift2_hours || 0) : parseFloat(common.shift2_hours || 0);
  const hrs   = s1 + s2;
  if (!spd || !pcm || !heads || !hrs) return 0;
  return Math.round((spd * 60) / (pcm * 100) * heads * hrs * 100) / 100;
};

const kgFor = (row, meters) => {
  const wgm = parseFloat(row.weight_gm_per_meter || 0);
  if (wgm <= 0 || meters <= 0) return 0;
  return Math.round((meters * wgm) / 10) / 100;
};

/**
 * @param {object} props
 * @param {Array}  props.splits             Prepared shard rows from ProductionEntry
 * @param {object} props.machine            Machine master doc (code, machine_model)
 * @param {number} props.machineTotalHeads  From MachineType.no_of_heads
 * @param {object} props.common             { shift1_hours, shift2_hours }
 * @param {func}   props.onCommonChange     Sets common shift hours
 * @param {func}   props.onRowChange        (idx, patch) → mutate one shard
 * @param {func}   props.onSaveBatch
 * @param {func}   props.onCancel
 */
export default function LoomSplitGrid({
  splits, machine, machineTotalHeads, common,
  onCommonChange, onRowChange, onSaveBatch, onCancel,
  saving = false,
}) {
  const derived = useMemo(() =>
    splits.map(r => {
      const meters = meterFor(r, common);
      return { ...r, _meters: meters, _kgs: kgFor(r, meters) };
    }),
    [splits, common]);

  const totalHeads = splits.reduce((s, r) => s + (parseInt(r.heads_allocated) || 0), 0);
  const totalMeters = derived.reduce((s, r) => s + (r.actual_meters ?? r._meters), 0);
  const totalKgs = derived.reduce((s, r) => s + (r._kgs || 0), 0);
  const headsOK = machineTotalHeads === 0 || totalHeads <= machineTotalHeads;
  const validHours = (parseFloat(common.shift1_hours || 0) + parseFloat(common.shift2_hours || 0)) > 0
                     || splits.some(r => r.override && (parseFloat(r.shift1_hours||0) + parseFloat(r.shift2_hours||0)) > 0);

  return (
    <div className="space-y-4" data-testid="loom-split-grid">
      {/* Common Shift Hours (cascade to all rows unless overridden) */}
      <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-0)]/60 px-3 py-2.5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Common Running Hours (cascade to all shards)</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Shift-1 hrs</label>
            <FormattedNumberInput
              step="0.1" min="0" max="12"
              data-testid="split-common-shift1"
              value={common.shift1_hours}
              onChange={e => onCommonChange({ ...common, shift1_hours: parseFloat(e.target.value) || 0 })}
              className={inp} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Shift-2 hrs</label>
            <FormattedNumberInput
              step="0.1" min="0" max="12"
              data-testid="split-common-shift2"
              value={common.shift2_hours}
              onChange={e => onCommonChange({ ...common, shift2_hours: parseFloat(e.target.value) || 0 })}
              className={inp} />
          </div>
        </div>
      </div>

      {/* Split Grid */}
      <div className="rounded-sm border border-[var(--border)] overflow-hidden">
        <table className="w-full text-xs" data-testid="split-grid-table">
          <thead>
            <tr className="bg-[var(--bg-0)]/60 uppercase tracking-widest text-[10px] text-zinc-500 border-b border-[var(--border)]">
              <th className="px-2 py-2 text-left">Split</th>
              <th className="px-2 py-2 text-left">SO / WO</th>
              <th className="px-2 py-2 text-left">Product</th>
              <th className="px-2 py-2 text-right">Heads</th>
              <th className="px-2 py-2 text-right">S-1 Hrs</th>
              <th className="px-2 py-2 text-right">S-2 Hrs</th>
              <th className="px-2 py-2 text-right">Calc m</th>
              <th className="px-2 py-2 text-right">Actual m</th>
              <th className="px-2 py-2 text-right">Kg</th>
              <th className="px-2 py-2 text-center">Override</th>
            </tr>
          </thead>
          <tbody>
            {derived.map((row, i) => (
              <tr key={row.plan_id} data-testid={`split-row-${i}`}
                  className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                <td className="px-2 py-2 font-mono text-[var(--accent)]">#{i + 1}</td>
                <td className="px-2 py-2">
                  <div className="font-mono text-white">{row.source_order_no || "—"}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{row.wo_no || "—"}</div>
                </td>
                <td className="px-2 py-2">
                  <div className="font-mono text-zinc-200 text-[11px]" title={row.product_code}>{row.product_code || "—"}</div>
                  <div className="text-[9px] text-zinc-500">{(row.pending_qty ?? 0).toLocaleString("en-IN")} m pending</div>
                </td>
                <td className="px-2 py-2 text-right">
                  <FormattedNumberInput
                    step="1" min="1"
                    data-testid={`split-row-heads-${i}`}
                    value={row.heads_allocated}
                    onChange={e => onRowChange(i, { heads_allocated: parseInt(e.target.value) || 0 })}
                    className={`${inp} w-16 text-right`} />
                </td>
                <td className="px-2 py-2 text-right">
                  {row.override ? (
                    <FormattedNumberInput step="0.1" min="0" max="12"
                      data-testid={`split-row-shift1-${i}`}
                      value={row.shift1_hours}
                      onChange={e => onRowChange(i, { shift1_hours: parseFloat(e.target.value) || 0 })}
                      className={`${inp} w-16 text-right`} />
                  ) : (
                    <span className="font-mono text-zinc-500">{common.shift1_hours || 0}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  {row.override ? (
                    <FormattedNumberInput step="0.1" min="0" max="12"
                      data-testid={`split-row-shift2-${i}`}
                      value={row.shift2_hours}
                      onChange={e => onRowChange(i, { shift2_hours: parseFloat(e.target.value) || 0 })}
                      className={`${inp} w-16 text-right`} />
                  ) : (
                    <span className="font-mono text-zinc-500">{common.shift2_hours || 0}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right font-mono text-zinc-300"
                    data-testid={`split-row-calc-${i}`}>{row._meters.toLocaleString("en-IN")}</td>
                <td className="px-2 py-2 text-right">
                  <FormattedNumberInput
                    step="0.01" min="0"
                    data-testid={`split-row-actual-${i}`}
                    value={row.actual_meters ?? row._meters}
                    onChange={e => onRowChange(i, { actual_meters: parseFloat(e.target.value) || 0 })}
                    className={`${inp} w-24 text-right`} />
                </td>
                <td className="px-2 py-2 text-right font-mono text-emerald-300"
                    data-testid={`split-row-kgs-${i}`}>{row._kgs.toFixed(2)}</td>
                <td className="px-2 py-2 text-center">
                  <input type="checkbox"
                    data-testid={`split-row-override-${i}`}
                    checked={!!row.override}
                    onChange={e => onRowChange(i, { override: e.target.checked })}
                    className="accent-[var(--accent)]" />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[var(--bg-0)]/40 font-semibold border-t-2 border-[var(--border)]">
              <td colSpan={3} className="px-2 py-2 uppercase tracking-widest text-[10px] text-zinc-500">Totals</td>
              <td className={`px-2 py-2 text-right font-mono ${headsOK ? "text-emerald-300" : "text-red-400"}`}
                  data-testid="split-total-heads">
                {totalHeads}{machineTotalHeads > 0 && ` / ${machineTotalHeads}`}
              </td>
              <td colSpan={3}></td>
              <td className="px-2 py-2 text-right font-mono text-white"
                  data-testid="split-total-meters">{totalMeters.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
              <td className="px-2 py-2 text-right font-mono text-emerald-300"
                  data-testid="split-total-kgs">{totalKgs.toFixed(2)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Validation banner */}
      {!headsOK && (
        <div data-testid="split-heads-error"
             className="rounded-sm border border-red-500/50 bg-red-500/10 px-3 py-2 text-[11px] text-red-300 font-mono">
          Head allocation {totalHeads} exceeds machine capacity {machineTotalHeads}. Reduce heads on any shard.
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--border)]">
        <button data-testid="split-cancel-btn" onClick={onCancel}
                className="h-10 px-4 border border-[var(--border)] text-zinc-300 text-sm rounded-sm hover:bg-[var(--bg-2)]">
          Cancel
        </button>
        <button data-testid="split-save-btn"
                disabled={!headsOK || !validHours || saving}
                onClick={() => onSaveBatch(derived)}
                className={`h-10 px-5 text-sm font-semibold rounded-sm flex items-center gap-2 ${
                  (!headsOK || !validHours || saving)
                    ? "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                    : "bg-[var(--accent)] hover:bg-blue-600 text-white"
                }`}>
          {saving ? "Saving…" : `Save ${splits.length}-Shard Batch`}
        </button>
      </div>
    </div>
  );
}
